import React, { useEffect, useState } from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { FormattedMessage } from 'react-intl';
import { ExtendedUser } from '../../../Types';
import UserBadge from '../../Dashboard/DashboardComponents/UserBadge';
import SideMenuLink from '@amzn/meridian/side-menu/side-menu-link';
import SideMenu from '@amzn/meridian/side-menu/side-menu';
import { useParams } from 'react-router';

interface UsersSidebarProps {
  directReports: ExtendedUser[];
  selectedDirectReport: ExtendedUser | undefined;
  onSelectDirectReport: (directReport: ExtendedUser | undefined) => void;
  sharedProfiles: ExtendedUser[];
  selectedSharedProfile: ExtendedUser | undefined;
  onSelectSharedProfile: (sharedProfile: ExtendedUser | undefined) => void;
  loading: boolean;
}

const UsersSidebar: React.FC<UsersSidebarProps> = ({
  directReports,
  selectedDirectReport,
  onSelectDirectReport,
  sharedProfiles,
  selectedSharedProfile,
  onSelectSharedProfile,
  loading,
}) => {
  const [openLinkHash, setOpenLinkHash] = useState<string | null>(null);
  const { userId: userIdFromUrl } = useParams() as {
    userId: string;
  };

  const handleSelectMyTeam = (href: string) => {
    onSelectDirectReport(undefined);
  };

  const handleSelectDirectReport = (directReport: ExtendedUser) => {
    onSelectDirectReport(directReport);
  };

  const handleSelectSharedProfile = (sharedProfile: ExtendedUser) => {
    onSelectSharedProfile(sharedProfile);
  };

  const handleToggleSharedProfiles = (href: string) => {
    // Toggle the link's open state when clicked multiple times
    setOpenLinkHash(openLinkHash === href ? null : href);
  };

  // Calculate selectedHash based on selectedDirectReport from parent
  const getSelectedHash = () => {
    if (!selectedDirectReport && !selectedSharedProfile) {
      return '#my-team';
    }

    // Check if it's a shared profile
    if (selectedSharedProfile) {
      return `#shared-profile-${selectedSharedProfile.userID}`;
    }
    // It's a direct report
    if (selectedDirectReport) {
      return `#direct-report-${selectedDirectReport.userID}`;
    }
    // Default hash return
    return '#my-team';
  };

  const selectedHash = getSelectedHash();

  const totalAssociates = directReports ? directReports.length : 0;

  // TODO - We also need to have if shared profile rendering is needed
  useEffect(() => {
    if (userIdFromUrl)
      if (directReports.find(dr => dr.userID === userIdFromUrl))
        onSelectDirectReport(
          directReports.find(dr => dr.userID === userIdFromUrl)
        );
  }, [userIdFromUrl]);

  return (
    <Box width='100%' height='100%' backgroundColor='background-secondary'>
      <Column>
        <SideMenu>
          {/* My Team - Shows all associates */}
          <SideMenuLink
            href='#my-team'
            selected={selectedHash === '#my-team'}
            onClick={handleSelectMyTeam}
            disabled={loading}
          >
            <Row height='48px' alignmentVertical='center'>
              <Text type='h100'>
                <FormattedMessage
                  id='cs_perfportal_training_my_team'
                  defaultMessage='My Team ({count} Associates)'
                  values={{ count: totalAssociates }}
                />
              </Text>
            </Row>
          </SideMenuLink>

          {/* Individual Direct Reports */}
          {directReports &&
            directReports.map(directReport => (
              <SideMenuLink
                key={directReport.userID}
                href={`#direct-report-${directReport.userID}`}
                selected={
                  selectedHash === `#direct-report-${directReport.userID}`
                }
                onClick={() => handleSelectDirectReport(directReport)}
                disabled={loading}
              >
                <Row alignmentHorizontal='start' width='100%'>
                  <UserBadge
                    username={directReport.alias}
                    thumbNail
                    thumbnailSize='small'
                  />
                  <Text truncate>{directReport.displayName}</Text>
                </Row>
              </SideMenuLink>
            ))}

          {/* Shared Profiles - Nested Sidebar */}
          <SideMenuLink
            href='#shared-profiles'
            selected={selectedHash === '#shared-profiles'}
            open={openLinkHash === '#shared-profiles'}
            onClick={handleToggleSharedProfiles}
            disabled={loading}
          >
            <Row height='48px' alignmentVertical='center'>
              <FormattedMessage
                id='cs_perfportal_training_shared_profiles'
                defaultMessage='Shared Profiles'
              />
            </Row>

            {/* Nested Shared Profile Links */}
            {sharedProfiles.map(sharedProfile => (
              <SideMenuLink
                key={sharedProfile.userID}
                href={`#shared-profile-${sharedProfile.userID}`}
                selected={
                  selectedHash === `#shared-profile-${sharedProfile.userID}`
                }
                onClick={() => handleSelectSharedProfile(sharedProfile)}
                onEscape={() => {
                  // Close the group
                  setOpenLinkHash(null);
                }}
                disabled={loading}
              >
                <Row alignmentHorizontal='start' width='100%'>
                  <UserBadge
                    username={sharedProfile.alias}
                    thumbNail
                    thumbnailSize='small'
                  />
                  <Text truncate>{sharedProfile.displayName}</Text>
                </Row>
              </SideMenuLink>
            ))}
          </SideMenuLink>
        </SideMenu>
      </Column>
    </Box>
  );
};

export default UsersSidebar;
